/**
 * VIVANCE · assets/vivance.js (módulo, carregado 1x por snippets/vivance-head.liquid)
 *
 * 1. addToCart(): adiciona linhas ao carrinho espelhando #processBatchAddToCart de product-form.js.
 *    Dispara CartLinesUpdateEvent com a promise resolvida pelo carrinho real, então o cart drawer
 *    nativo abre, re-renderiza (Section Rendering) e o contador do header atualiza sozinhos.
 * 2. <vivance-add-to-cart>: envolve o form do card Vivance e troca o POST cheio pelo addToCart().
 * 3. Reveal por scroll com fail-open: [data-vv-reveal] só fica oculto depois que a raiz .vivance
 *    recebe [data-vv-ready], e tudo aparece em até 2,5 s mesmo sem IntersectionObserver.
 * 4. formatMoney(): formatação no padrão da loja (shop.money_format), para somas feitas no cliente.
 * 5. <vivance-sync data-block-id>: acompanha a troca de variante re-renderizando o próprio HTML a partir
 *    do documento que o tema já buscou (padrão de assets/product-price.js). Nenhum preço é calculado em JS.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney as themeFormatMoney } from '@theme/money-formatting';

const ADDED_DURATION = 1600;

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    // @ts-ignore — método público do CartItemsComponent
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Falha ao ler o carrinho: ${response.status}`);
  return response.json();
}

/**
 * @param {Array<{id: number|string, quantity?: number}>} items
 * @param {Element} [source] - elemento que origina o evento (bolha até o document)
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export async function addToCart(items, source = document.body) {
  const lines = items.map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) || 1 }));
  if (!lines.length) return { ok: false };

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: cartSectionIds() }),
    });
    const data = await response.json();
    const cart = await fetchCart();
    const didError = Boolean(data.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'vivance',
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        sections: didError ? undefined : data.sections,
        didError,
      },
    });

    return didError ? { ok: false, message: data.description || data.message } : { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'Não foi possível adicionar agora. Tente novamente.' };
  }
}

/**
 * @param {number} cents
 * @param {string} [format]
 */
export function formatMoney(cents, format) {
  const moneyFormat = format || document.documentElement.dataset.vvMoneyFormat || 'R$ {{amount_with_comma_separator}}';
  const currency = window.Shopify?.currency?.active || 'BRL';
  return themeFormatMoney(Math.round(cents), moneyFormat, currency);
}

class VivanceAddToCart extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('[type="submit"]');
    const label = button?.querySelector('[data-label]');
    const error = this.querySelector('[data-error]');
    const id = form.querySelector('[name="id"]')?.value;
    const quantity = form.querySelector('[name="quantity"]')?.value || 1;

    button?.classList.add('is-loading');
    if (error) error.hidden = true;

    const result = await addToCart([{ id, quantity }], this);

    button?.classList.remove('is-loading');
    if (result.ok) {
      if (label && button) {
        const original = label.textContent;
        label.textContent = button.dataset.addedLabel || 'Na sacola';
        button.classList.add('is-added');
        setTimeout(() => {
          label.textContent = original;
          button.classList.remove('is-added');
        }, ADDED_DURATION);
      }
    } else if (error && result.message) {
      error.textContent = result.message;
      error.hidden = false;
    }
  };
}

if (!customElements.get('vivance-add-to-cart')) customElements.define('vivance-add-to-cart', VivanceAddToCart);


/* ---------- Troca de variante (padrão de product-price.js) ---------- */
class VivanceSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.newProduct) this.dataset.productId = detail.newProduct.id;
        else if (detail.productId && this.dataset.productId && String(detail.productId) !== this.dataset.productId) return;
        const fresh = detail.html.querySelector(`vivance-sync[data-block-id="${this.dataset.blockId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

if (!customElements.get('vivance-sync')) customElements.define('vivance-sync', VivanceSync);

/* ---------- Reveal (fail-open) ---------- */
function initReveal(scope = document) {
  const roots = scope.querySelectorAll('.vivance:has([data-vv-reveal]):not([data-vv-ready])');
  roots.forEach((root) => {
    const targets = root.querySelectorAll('[data-vv-reveal]');
    root.setAttribute('data-vv-ready', '');
    const show = (el) => el.classList.add('is-in');

    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(show);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -5% 0px' }
    );
    targets.forEach((el) => io.observe(el));
    setTimeout(() => targets.forEach(show), 2500);
  });
}

initReveal();
document.addEventListener('shopify:section:load', (event) => initReveal(/** @type {Element} */ (event.target)));

window.Vivance = Object.assign(window.Vivance || {}, { addToCart, formatMoney, initReveal });
document.dispatchEvent(new CustomEvent('vivance:ready'));
